import React, { FunctionComponent } from "react";
import styled from "styled-components";
import Image from "next/image";

import { colours } from "@/utils";
import { Device } from "@/utils/device";
import { CL, CM, CS } from "@/components/Typography";
import RxLink from "../ui/inputs/RxLink";
import { Article } from "@/types/article/Article";

type StyleProps = {
  flexDirection?: string;
  imageWidth?: string;
};

interface PostArticleCardProps extends Article, StyleProps {
  title: string;
  url: string;
  children?: React.ReactNode;
  externalLink?: boolean;
  onClick?: () => void;
  topic?: string;
}

const PostArticleCard: FunctionComponent<PostArticleCardProps> = (props) => {
  if (!props.heroImage) return null;

  return (
    <RxLink
      href={props.externalLink ? props.url : `/edit/articles/${props.url}`}
    >
      <S.Container
        data-cy={props.sys.id + "-article-card"}
        {...props}
        tabIndex={0}
      >
        <S.ImageContainer {...props}>
          <Image
            src={props.heroImage.image.url}
            alt={`image for ${props.title} article`}
            layout="fill"
            objectFit="cover"
            objectPosition="right"
          />
        </S.ImageContainer>
        <S.ContentWrap>
          <CM weight="700" colour={colours.charcoalMid}>
            {props.topic}
          </CM>
          <S.Title weight="700">{props.title}</S.Title>
          {props.author && (
            <CS colour={colours.charcoalMid}>By {props.author.name}</CS>
          )}
        </S.ContentWrap>
        {props.children}
      </S.Container>
    </RxLink>
  );
};

const S = () => {};

S.Container = styled.article<StyleProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
  transition: all 0.3s ease-out;

  &:hover {
    transform: scale(1.02);
    filter: brightness(1.1);
  }

  &:focus-visible {
    outline: 2px solid ${colours.charcoal};
    transform: scale(1.02);
    filter: brightness(1.1);
    border-radius: 4px;
  }

  @media (min-width: ${Device.tablet}) {
    gap: 5px;
  }

  @media (min-width: ${Device.mobile}) {
    flex-direction: ${(p) => p.flexDirection || "row"};
  }
`;

S.ImageContainer = styled.div<StyleProps>`
  position: relative;
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
  flex: none;
  box-shadow: 0px 4px 4px 0px #c3afa1;
  aspect-ratio: 1;

  @supports not (aspect-ratio: 1) {
    &::before {
      float: left;
      padding-top: 100%;
      content: "";
    }
    &::after {
      display: block;
      content: "";
      clear: both;
    }
  }

  @media (min-width: ${Device.mobile}) {
    width: ${(p) => p.imageWidth || "30%"};
  }
`;

S.ContentWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 5px 10px;
  overflow: hidden;

  @media (min-width: ${Device.tablet}) {
    gap: 1rem;
  }

  @media (min-width: ${Device.desktop}) {
    padding: 2rem 1.5rem;
  }
`;

S.Title = styled(CL)`
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (min-width: ${Device.tablet}) {
    -webkit-line-clamp: 3;
  }
`;

export default PostArticleCard;
